import React, { memo, useCallback, useContext, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { getAssetFromItem } from '../assets/utils';
import { EditModeContext } from '../edit-mode';
import { FEATURE_SHIFT_KEY_TO_OVERRIDE_ASPECT_RATIO_LOCK } from '../flags';
import { calculateResizeSnap, useSmartGuidesOptional } from '../smart-guides';
import { changeItem } from '../state/actions/change-item';
import {
  getKeepAspectRatio,
  getOriginalAspectRatio,
} from '../utils/aspect-ratio';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { isLeftClick } from '../utils/is-left-click';
import {
  useCurrentStateAsRef,
  useDimensions,
  useWriteContext,
} from '../utils/use-context';
import { resizeVisibleRectToItem, type VisibleRect } from './resize-geometry';

const REAL_SIZE = 8;

const ResizeHandleUnmemoized: React.FC<{
  type:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top'
    | 'right'
    | 'bottom'
    | 'left';
  width: number;
  height: number;
  left: number;
  top: number;
  itemId: string;
}> = ({ type, width, height, left, top, itemId }) => {
  const scale = useCanvasTransformationScale();
  const size = Math.round(REAL_SIZE / scale);

  const { setState } = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();
  const { compositionWidth, compositionHeight } = useDimensions();
  const smartGuides = useSmartGuidesOptional();

  const sizeStyle: React.CSSProperties = useMemo(() => {
    // edge handle은 corner 공간을 제외한 전체 edge를 범위로 해야 함
    if (type === 'top' || type === 'bottom') {
      return {
        height: size,
        width: width - size, // corner handle space를 제외한 전체 width
      };
    }
    if (type === 'left' || type === 'right') {
      return {
        height: height - size, // corner handle space를 제외한 전체 height
        width: size,
      };
    }
    // corner handle은 원래 크기를 유지
    return {
      height: size,
      width: size,
    };
  }, [size, type, width, height]);

  const margin = -size / 2 - 1 / scale;

  const styleWithoutBorder: React.CSSProperties = useMemo(() => {
    if (type === 'top-left') {
      return {
        ...sizeStyle,
        marginLeft: margin,
        marginTop: margin,
        cursor: 'nwse-resize',
      };
    }
    if (type === 'top-right') {
      return {
        ...sizeStyle,
        marginTop: margin,
        marginRight: margin,
        right: 0,
        cursor: 'nesw-resize',
      };
    }
    if (type === 'bottom-left') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        marginLeft: margin,
        bottom: 0,
        cursor: 'nesw-resize',
      };
    }
    if (type === 'bottom-right') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        marginRight: margin,
        right: 0,
        bottom: 0,
        cursor: 'nwse-resize',
      };
    }
    if (type === 'top') {
      return {
        ...sizeStyle,
        marginTop: margin,
        left: size / 2, // 왼쪽 corner handle 이후부터 시작
        cursor: 'ns-resize',
      };
    }
    if (type === 'bottom') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        left: size / 2, // 왼쪽 corner handle 이후부터 시작
        bottom: 0,
        cursor: 'ns-resize',
      };
    }
    if (type === 'left') {
      return {
        ...sizeStyle,
        marginLeft: margin,
        top: size / 2, // 위쪽 corner handle 이후부터 시작
        cursor: 'ew-resize',
      };
    }
    if (type === 'right') {
      return {
        ...sizeStyle,
        marginRight: margin,
        top: size / 2, // 위쪽 corner handle 이후부터 시작
        right: 0,
        cursor: 'ew-resize',
      };
    }

    throw new Error('Unknown type: ' + JSON.stringify(type));
  }, [margin, sizeStyle, type, size]);

  const style: React.CSSProperties = useMemo(() => {
    // edge handle은 보이지 않아야 함
    const isEdgeHandle =
      type === 'top' ||
      type === 'bottom' ||
      type === 'left' ||
      type === 'right';

    return {
      ...styleWithoutBorder,
      // corner handle에만 경계선 표시
      border: isEdgeHandle
        ? 'none'
        : `${1 / scale}px solid var(--color-editor-starter-accent)`,
    };
  }, [scale, styleWithoutBorder, type]);

  const calculateResizedVisibleRect = useCallback(
    ({
      offsetX,
      naturalOffsetY,
      lockAspectRatio,
      aspectRatio,
    }: {
      offsetX: number;
      naturalOffsetY: number;
      lockAspectRatio: boolean;
      aspectRatio: number;
    }): VisibleRect => {
      let newVisibleWidth: number;
      let newVisibleHeight: number;
      let newVisibleLeft: number;
      let newVisibleTop: number;

      if (type === 'top' || type === 'bottom') {
        const heightOffset = type === 'top' ? -naturalOffsetY : naturalOffsetY;
        newVisibleHeight = Math.round(height + heightOffset);

        if (lockAspectRatio) {
          newVisibleWidth = Math.round(newVisibleHeight * aspectRatio);
          newVisibleLeft = Math.round(left - (newVisibleWidth - width) / 2);
        } else {
          newVisibleWidth = width;
          newVisibleLeft = left;
        }

        newVisibleTop =
          type === 'top' ? Math.round(top - (newVisibleHeight - height)) : top;
      } else if (type === 'left' || type === 'right') {
        const widthOffset = type === 'left' ? -offsetX : offsetX;
        newVisibleWidth = Math.round(width + widthOffset);

        if (lockAspectRatio) {
          newVisibleHeight = Math.round(newVisibleWidth / aspectRatio);
          newVisibleTop = Math.round(top - (newVisibleHeight - height) / 2);
        } else {
          newVisibleHeight = height;
          newVisibleTop = top;
        }

        newVisibleLeft =
          type === 'left' ? Math.round(left - (newVisibleWidth - width)) : left;
      } else {
        const unadjustedOffsetY = lockAspectRatio
          ? offsetX / aspectRatio
          : naturalOffsetY;
        const offsetY =
          lockAspectRatio && (type === 'bottom-left' || type === 'top-right')
            ? -unadjustedOffsetY
            : unadjustedOffsetY;

        newVisibleWidth = Math.round(
          width +
            (type === 'bottom-left' || type === 'top-left'
              ? -offsetX
              : offsetX),
        );
        newVisibleHeight = Math.round(
          height +
            (type === 'top-left' || type === 'top-right' ? -offsetY : offsetY),
        );
        newVisibleLeft = Math.round(
          left + (type === 'bottom-left' || type === 'top-left' ? offsetX : 0),
        );
        newVisibleTop = Math.round(
          top + (type === 'top-left' || type === 'top-right' ? offsetY : 0),
        );
      }

      return {
        left: newVisibleLeft,
        top: newVisibleTop,
        width: Math.max(1, newVisibleWidth),
        height: Math.max(1, newVisibleHeight),
      };
    },
    [height, left, top, type, width],
  );

  const onPointerDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isLeftClick(e)) {
        return;
      }

      const initialX = e.clientX;
      const initialY = e.clientY;
      const initialItem = stateAsRef.current.undoableState.items[itemId];
      let didResize = false;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        const naturalOffsetY = (pointerMoveEvent.clientY - initialY) / scale;

        const currentState = stateAsRef.current;
        const currentItem = currentState.undoableState.items[itemId];
        if (!currentItem) {
          return;
        }

        const keepAspectRatio = getKeepAspectRatio(currentItem);
        const lockAspectRatio =
          (FEATURE_SHIFT_KEY_TO_OVERRIDE_ASPECT_RATIO_LOCK &&
            pointerMoveEvent.shiftKey) !== keepAspectRatio;
        const asset = getAssetFromItem({
          item: currentItem,
          assets: currentState.undoableState.assets,
        });
        const aspectRatio = getOriginalAspectRatio({
          item: currentItem,
          asset,
        });

        let nextVisibleRect = calculateResizedVisibleRect({
          offsetX,
          naturalOffsetY,
          lockAspectRatio,
          aspectRatio,
        });

        if (smartGuides) {
          const snapResult = calculateResizeSnap({
            newLeft: nextVisibleRect.left,
            newTop: nextVisibleRect.top,
            newWidth: nextVisibleRect.width,
            newHeight: nextVisibleRect.height,
            canvasWidth: compositionWidth,
            canvasHeight: compositionHeight,
            handleType: type,
          });

          nextVisibleRect = {
            left: snapResult.snappedLeft,
            top: snapResult.snappedTop,
            width: snapResult.snappedWidth,
            height: snapResult.snappedHeight,
          };
          smartGuides.setActiveGuides(snapResult.activeGuides);
        }

        setState({
          update: (state) => {
            return changeItem(state, itemId, (i) => {
              const updatedItemBase = resizeVisibleRectToItem(
                i,
                nextVisibleRect,
              );
              if (updatedItemBase.left === i.left && updatedItemBase.top === i.top &&
                  updatedItemBase.width === i.width && updatedItemBase.height === i.height) return i;
              didResize = true;

              const updatedItem = {
                ...updatedItemBase,
                ...(updatedItemBase.type === 'text'
                  ? { resizeOnEdit: false }
                  : {}),
              };
              return updatedItem;
            });
          },
          commitToUndoStack: false,
        });
      };

      const onPointerUp = () => {
        smartGuides?.clearGuides();
        window.removeEventListener('pointermove', onPointerMove);
        // A drag back to the original item must retain the existing Redo branch.
        if (didResize && !isEqual(initialItem, stateAsRef.current.undoableState.items[itemId])) {
          setState({update: state => state, commitToUndoStack: true});
        }
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });

      window.addEventListener('pointerup', onPointerUp, {
        once: true,
      });
    },
    [
      compositionHeight,
      compositionWidth,
      calculateResizedVisibleRect,
      height,
      itemId,
      left,
      scale,
      setState,
      stateAsRef,
      smartGuides,
      top,
      type,
      width,
    ],
  );

  const isEdgeHandle =
    type === 'top' || type === 'bottom' || type === 'left' || type === 'right';

  // draw/hand 모드에서는 아이템 인터랙션을 차단한다(outline div 의 pointerEvents 게이팅과
  // 동일 조건). 핸들이 outline div 의 형제 레이어로 분리되며 상속이 끊겨 여기서 직접 건다.
  const { editMode } = useContext(EditModeContext);
  const interactive =
    editMode !== 'draw-solid' &&
    editMode !== 'draw-blur' &&
    editMode !== 'hand';

  return (
    // 부모 핸들 레이어가 pointerEvents none 이라 핸들만 auto 로 되살린다.
    <div
      onPointerDown={onPointerDown}
      style={style}
      className={`${interactive ? 'pointer-events-auto' : 'pointer-events-none'} absolute ${isEdgeHandle ? '' : 'bg-white'}`}
    ></div>
  );
};

export const ResizeHandle = memo(ResizeHandleUnmemoized);
